import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useMotion';

export default function BackgroundFilm({ content, sectionRef }) {
  const container = useRef(null);
  const video = useRef(null);
  const reducedMotion = useReducedMotion();
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const allowAutoplay = !reducedMotion && !saveData;

  useEffect(() => {
    // The film is fixed to the viewport; playback follows its content section.
    const target = sectionRef ? sectionRef.current : container.current;
    if (!target) return;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.05 });
    observer.observe(target);
    return () => observer.disconnect();
  }, [sectionRef]);

  useEffect(() => {
    if (visible && allowAutoplay) setLoaded(true);
  }, [visible, allowAutoplay]);

  useEffect(() => {
    const film = video.current;
    if (!film) return;
    const update = () => {
      if (visible && !document.hidden && allowAutoplay) {
        const promise = film.play();
        // The poster remains available if the browser blocks autoplay.
        if (promise) promise.catch(() => {});
      } else {
        film.pause();
      }
    };
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, [loaded, visible, allowAutoplay]);

  const revealFrame = event => {
    // Keep the poster visible until the browser has an actual frame to show.
    // This stays ready when playback pauses outside the section.
    if (event.currentTarget.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      setReady(true);
    }
  };

  return (
    <div className="background-film" ref={container} aria-hidden="true">
      <img className="background-film__poster" src={content.poster} alt="" loading="lazy" />
      {loaded && !failed && (
        <video ref={video} className={`background-film__video${ready ? ' is-ready' : ''}`} muted loop playsInline preload="none"
          poster={content.poster} tabIndex={-1} onLoadedData={revealFrame} onCanPlay={revealFrame}
          onError={() => setFailed(true)}>
          <source src={content.video} type="video/mp4" />
        </video>
      )}
      <div className="background-film__shade" />
    </div>
  );
}
